import { Wrench } from "lucide-react";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../../components/ui/toggle-group/toggle-group";
import { useFailures } from "../../../queries/nc/use-failures";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form/form";
import { useTranslation } from "react-i18next";
import { t, TFunction } from "i18next";
import { Skeleton } from "../../../components/ui/skeleton/skeleton";
import { QueryError } from "@/components/ui/query-error/query-error";

const failuresSchema = (t: TFunction<"translation", undefined>) =>
  z.object({
    failures: z.array(z.string()).min(1, t("nc.failures.error")),
  });

export const useFailuresForm = () => {
  const { t } = useTranslation();
  return useForm<z.infer<ReturnType<typeof failuresSchema>>>({
    resolver: zodResolver(failuresSchema(t)),
    defaultValues: {
      failures: [],
    },
  });
};

export function SelectFailures({
  form,
}: {
  form: ReturnType<typeof useFailuresForm>;
}) {
  const { failures, isPending, error, refetch } = useFailures();
  const sortedFailures = failures?.sort(
    (a, b) => Number(a.NId) - Number(b.NId)
  );

  if (isPending) {
    return (
      <div className="flex flex-row flex-wrap  gap-2.5 gap-y-1.5">
        {[...Array(20)].map((_, i) => (
          <Skeleton className="h-[42px] basis-[calc(50%-8px)]" key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <QueryError
        resource={t("nc.failures.ncFailure")}
        errors={[error]}
        retries={[refetch]}
      />
    );
  }

  return (
    <Form {...form}>
      <FormField
        control={form.control}
        name="failures"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <ToggleGroup
                type="multiple"
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="grid sm:grid-cols-2"
              >
                {sortedFailures?.map((failure) => (
                  <ToggleGroupItem key={failure.Id} value={failure.NId ?? ""}>
                    <Wrench className="flex-shrink-0" />
                    <div className="grid grid-cols-[30px_auto] items-center gap-1 text-left">
                      <span className="text-base font-semibold">
                        {failure.NId}
                      </span>
                      {failure.Name}
                    </div>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </Form>
  );
}
